import { Box, Button, Card, CardContent, Divider, Grid, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useParams, useNavigate } from "react-router-dom";
import { getAllPackages, getPackagesByLocation } from "../../api/packageAPI";
import { getSinglePlace, UpdatePlacesPacakges } from "../../api/placeApi";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PackageUploadInPlaces = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [singleData, setsingleData] = useState({});
  const [locationData, setLocationData] = useState([]);
  const [pkgData, setPkgData] = useState([]);
  const [packageIds, setPackageIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInfo, setSearchInfo] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getSinglePlace(id);
        const placeData = res?.data || res?.place || res || {};
        setsingleData(placeData);

        const extractedPackageIds =
          placeData?.packages?.package_ids ||
          placeData?.details?.packages?.package_ids ||
          [];

        const normalizedPackageIds = (Array.isArray(extractedPackageIds) ? extractedPackageIds : []).map(
          (pkg) => (typeof pkg === "object" && pkg?._id ? pkg._id : pkg)
        );
        setPackageIds(normalizedPackageIds);

        if (placeData.zone) {
          try {
            const locRes = await getPackagesByLocation(placeData.zone);
            setLocationData(Array.isArray(locRes?.data) ? locRes.data : Array.isArray(locRes) ? locRes : []);
          } catch (error) {
            console.error("Failed to fetch location packages:", error);
            setLocationData([]);
          }
        }

        try {
          const allPackages = await getAllPackages();
          setPkgData(Array.isArray(allPackages?.data) ? allPackages.data : Array.isArray(allPackages) ? allPackages : []);
        } catch (error) {
          console.error("Failed to fetch all packages:", error);
          setPkgData([]);
        }
      } catch (error) {
        console.error("Failed to fetch place details:", error);
      }
    };
    fetchData();
  }, [id]);

  const handleAdd = useCallback(async (packageId) => {
    setPackageIds((prev) => [...prev, packageId]);
    const obj = {
      "add": [packageId],
    }

    if (packageId) {
      await UpdatePlacesPacakges(id, obj);
    }

  }, [id]);

  const handleRemove = useCallback(async (packageId) => {
    setPackageIds((prev) => prev.filter((pId) => pId !== packageId));
    const obj = {
      "remove": [packageId],
    }

    if (packageId) {

      await UpdatePlacesPacakges(id, obj);
    }
  }, [id]);

  // Table data must keep a stable reference: a new array every render makes
  // react-table reset its page index, re-render, and loop forever (freezes the tab)
  const finalPackages = useMemo(() => (pkgData || []).filter((singlePackage) =>
    singlePackage?._id && (packageIds || []).includes(singlePackage._id)
  ), [pkgData, packageIds]);

  const filterPackage = useMemo(() => (locationData || []).filter((data) => {
    return (data?.label || data?.name || "").toLowerCase().includes((searchTerm || "").toLowerCase());
  }), [locationData, searchTerm]);

  const filteredDestinationPkgs = useMemo(() => finalPackages.filter((data) => {
    return (data?.label || data?.name || "").toLowerCase().includes((searchInfo || "").toLowerCase());
  }), [finalPackages, searchInfo]);

  // Column definitions for Preferred Packages table
  const preferredPackagesColumns = useMemo(() => [
    {
      accessorKey: "label",
      header: "Package Name",
      cell: ({ getValue, row }) => {
        return getValue() || row?.original?.name || "-";
      },
    },
    {
      accessorKey: "duration",
      header: "Duration",
      cell: ({ getValue }) => {
        const duration = getValue();
        return duration ? `${duration - 1}N ${duration}D` : "-";
      },
    },
    {
      id: "action",
      header: "Action",
      cell: ({ row }) => {
        const singlePackage = row?.original;
        if (!singlePackage?._id) return null;
        const isIncluded = (packageIds || []).includes(singlePackage._id);
        return (
          isIncluded ? (
            <Button variant="contained" color="error" onClick={() => handleRemove(singlePackage._id)}>
              <DeleteIcon />
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={() => handleAdd(singlePackage._id)}>
              <AddIcon />
            </Button>
          )
        );
      },
    },
  ], [packageIds, handleAdd, handleRemove]);

  // Column definitions for Updated Packages table
  const updatedPackagesColumns = useMemo(() => [
    {
      accessorKey: "label",
      header: "Package Name",
      cell: ({ getValue, row }) => {
        return getValue() || row?.original?.name || "-";
      },
    },
    {
      accessorKey: "duration",
      header: "Duration",
      cell: ({ getValue }) => {
        const duration = getValue();
        return duration ? `${duration - 1}N ${duration}D` : "-";
      },
    },
    {
      id: "action",
      header: "Action",
      cell: ({ row }) => {
        const pkg = row?.original;
        if (!pkg?._id) return null;
        return (
          <Button variant="contained" color="error" onClick={() => handleRemove(pkg._id)}>
            <DeleteIcon />
          </Button>
        );
      },
    },
  ], [handleRemove]);

  // Table instances
  const preferredPackagesTable = useReactTable({
    data: filterPackage,
    columns: preferredPackagesColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => row?._id || String(index),
  });

  const updatedPackagesTable = useReactTable({
    data: filteredDestinationPkgs,
    columns: updatedPackagesColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => row?._id || String(index),
  });

  return (
    <>
      <Box sx={{ px: 3, pt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            // Go back to the Places list with its previous filters; fall back if opened directly
            if (window.history.state?.idx > 0) navigate(-1);
            else navigate("/places/view");
          }}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Back to Places
        </Button>
      </Box>

      <Box display="flex" justifyContent="center">
        <Box m={2} mb={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Typography variant="h3" fontWeight="bold">
            {singleData?.name || ""}
          </Typography>
          <Typography variant="h6" fontWeight="bold">Add/Update Packages</Typography>
        </Box>
      </Box>
      <Grid container gap={5} alignItems="stretch">
        <Grid item xs={5}>
          <Card sx={{ width: "100%", mb: 4, display: 'flex', flexDirection: "column", alignItems: "stretch" }}>

            <Typography variant="h5" fontWeight="bold" align="center" sx={{ mt: 2, mb: 2 }}>Preferred Packages</Typography>



            <TextField
              sx={{ mx: 2 }}
              label="Search Packages"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}

            />



            <CardContent sx={{ flexGrow: 1 }}>
              <Table>
                <TableHead>
                  {preferredPackagesTable.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <TableCell key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {preferredPackagesTable.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={preferredPackagesColumns.length} align="center">
                        No Data Found
                      </TableCell>
                    </TableRow>
                  ) : (
                    preferredPackagesTable.getRowModel().rows.map(row => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Divider orientation="vertical" variant="middle" flexItem />

        <Grid item xs={5}>
          <Card sx={{ width: "100%", mb: 4, display: 'flex', flexDirection: "column", alignItems: "stretch" }}>

            <Typography variant="h5" fontWeight="bold" align="center" sx={{ mt: 2, mb: 2 }}>Updated Packages</Typography>
            <TextField
              sx={{ mx: 2 }}
              label="Search Packages"
              variant="outlined"
              size="small"
              value={searchInfo}
              onChange={(e) => setSearchInfo(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <CardContent sx={{ flexGrow: 1 }}>
              <Table>
                <TableHead>
                  {updatedPackagesTable.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <TableCell key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {updatedPackagesTable.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={updatedPackagesColumns.length} align="center">
                        No Data Found
                      </TableCell>
                    </TableRow>
                  ) : (
                    updatedPackagesTable.getRowModel().rows.map(row => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};


export default PackageUploadInPlaces;
